package org.gstechnovos.jexonplastech.sections.products.model

import org.gstechnovos.jexonplastech.util.Res

// 1. UNIFIED DATA MODEL
data class Product(
    val id: String, val name: String, val category: String, val modelNumber: String, // Also acts as the URL Slug
    val images: List<String>, // Grid uses images.first(), Details page uses the whole list for the slider
    val shortDescription: String, val features: List<String>, val specifications: Map<String, String>
)

// 2. CENTRALIZED CATALOG DATABASE
// Both ProductsGridSection and ProductDetailsPage will fetch from this single list.
object GlobalCatalog {
    val items = listOf(
        // ==========================================
        // CLEANING BRUSHES
        // ==========================================
        Product(
            id = "1",
            name = "Square Brush",
            category = "Cleaning Brushes",
            modelNumber = "CB-001",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.SQUARE_BRUSH,Res.Image.Products.CLEANING_BRUSH.TYPE_BRUSH_8 ,Res.Image.Products.CLEANING_BRUSH.OVAL_BRUSH ),
            shortDescription = "A heavy-duty square cleaning brush designed for maximum surface area scrubbing. Ideal for tough stains on tiles and concrete.",
            features = listOf(
                "100% BPA-Free plastic construction",
                "High-density nylon bristles for deep cleaning",
                "Ergonomic anti-slip handle"
            ),
            specifications = mapOf(
                "Material" to "Polypropylene (PP)",
                "Usage" to "Heavy Duty Floor/Wall Scrubbing",
                "Colors Available" to "Multiple"
            )
        ), Product(
            id = "2",
            name = "8-Type Brush",
            category = "Cleaning Brushes",
            modelNumber = "CB-002",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.TYPE_BRUSH_8),
            shortDescription = "Unique figure-8 design allows for an excellent grip and concentrated scrubbing power in high-friction areas.",
            features = listOf(
                "Figure-8 ergonomic grip",
                "Stiff bristles for grout and tile",
                "Chemical resistant plastic"
            ),
            specifications = mapOf(
                "Material" to "High-Grade Virgin Plastic",
                "Shape" to "Figure 8",
                "Usage" to "Household & Commercial"
            )
        ), Product(
            id = "3",
            name = "Oval Brush",
            category = "Cleaning Brushes",
            modelNumber = "CB-003",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.OVAL_BRUSH),
            shortDescription = "Classic oval-shaped brush suitable for everyday household cleaning, laundry, and fabric scrubbing.",
            features = listOf("Gentle yet effective bristles", "Comfortable palm grip", "Durable and long-lasting"),
            specifications = mapOf(
                "Shape" to "Oval",
                "Application" to "Laundry & General Cleaning",
                "Colors Available" to "Multiple"
            )
        ), Product(
            id = "4",
            name = "Pan Brush",
            category = "Cleaning Brushes",
            modelNumber = "CB-004",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.PAN_BRUSH),
            shortDescription = "Designed specifically with a long handle to clean pans, pots, and kitchen surfaces without getting hands dirty.",
            features = listOf("Extended ergonomic handle", "Food-safe materials", "Grease-resistant bristles"),
            specifications = mapOf(
                "Material" to "Food-grade Plastic",
                "Application" to "Kitchen Utensils",
                "Colors Available" to "Multiple"
            )
        ), Product(
            id = "5",
            name = "Ground Brush",
            category = "Cleaning Brushes",
            modelNumber = "CB-005",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.GROUND_BRUSH),
            shortDescription = "Large, aggressive ground brush built to scrub hard outdoor surfaces, driveways, and industrial floors.",
            features = listOf("Extra-stiff industrial bristles", "Wide scrubbing surface", "Crack-resistant body"),
            specifications = mapOf(
                "Usage" to "Outdoor & Industrial",
                "Material" to "Heavy-duty PP",
                "Colors Available" to "Multiple"
            )
        ), Product(
            id = "6",
            name = "Wooden Brush (6\")",
            category = "Cleaning Brushes",
            modelNumber = "CB-006",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.WOODEN_BRUSH_6),
            shortDescription = "Premium 6-inch wooden brush combining traditional aesthetics with industrial-grade scrubbing power. Available in hard and soft bristles.",
            features = listOf(
                "Solid wood base",
                "Available in Hard or Soft bristle options",
                "Environmentally friendly body"
            ),
            specifications = mapOf(
                "Size" to "6 Inches",
                "Material" to "Wood & Synthetic Bristles",
                "Bristle Type" to "Hard / Soft"
            )
        ), Product(
            id = "7",
            name = "Wooden Brush (7\")",
            category = "Cleaning Brushes",
            modelNumber = "CB-007",
            images = listOf(Res.Image.Products.CLEANING_BRUSH.WOODEN_BRUSH_7),
            shortDescription = "Extended 7-inch wooden brush for larger surface area coverage. Built to withstand heavy commercial use.",
            features = listOf(
                "Extended solid wood base",
                "Available in Hard or Soft bristle options",
                "Comfortable grip contour"
            ),
            specifications = mapOf(
                "Size" to "7 Inches",
                "Material" to "Wood & Synthetic Bristles",
                "Bristle Type" to "Hard / Soft"
            )
        ),

        // ==========================================
        // TOILET BRUSHES
        // ==========================================
        Product(
            id = "8",
            name = "Medium Single Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-001",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.MEDIUM_SINGLE_HOCKEY),
            shortDescription = "Medium-sized hockey-style toilet brush designed for efficient under-rim cleaning and everyday sanitation.",
            features = listOf(
                "Curved hockey design for rim access",
                "Stiff, stain-removing bristles",
                "Long handle for hygiene"
            ),
            specifications = mapOf("Style" to "Single Hockey", "Size" to "Medium", "Material" to "Virgin PP")
        ), Product(
            id = "9",
            name = "Big Single Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-002",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.BIG_SINGLE_HOCKEY),
            shortDescription = "Large single hockey toilet brush providing maximum reach and scrubbing strength for commercial restrooms.",
            features = listOf("Extended reach design", "Heavy-duty bristles", "Ergonomic grip handle"),
            specifications = mapOf("Style" to "Single Hockey", "Size" to "Large", "Material" to "Virgin PP")
        ), Product(
            id = "10",
            name = "Small Double Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-003",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.SMALL_DOUBLE_HOCKEY),
            shortDescription = "Dual-sided hockey brush for comprehensive cleaning, allowing users to scrub the bowl and under the rim simultaneously.",
            features = listOf("Dual-bristle heads", "Compact and maneuverable", "Hygienic storage design"),
            specifications = mapOf("Style" to "Double Hockey", "Size" to "Small", "Usage" to "Household Bathrooms")
        ), Product(
            id = "11",
            name = "Big Double Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-004",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.BIG_DOUBLE_HOCKEY),
            shortDescription = "Our ultimate commercial toilet brush featuring large dual-sided heads for rapid, deep sanitation.",
            features = listOf("Large dual-bristle heads", "Thick, non-bending handle", "Maximum scrubbing efficiency"),
            specifications = mapOf("Style" to "Double Hockey", "Size" to "Large", "Usage" to "Commercial Bathrooms")
        ), Product(
            id = "12",
            name = "Black Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-005",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.BLACK_HOCKEY),
            shortDescription = "A sleek, black hockey-style toilet brush designed to resist discoloration while maintaining high performance.",
            features = listOf("Stain-masking black bristles", "Curved rim-cleaning head", "Modern aesthetic"),
            specifications = mapOf("Style" to "Hockey", "Color" to "Black", "Material" to "Virgin PP")
        ), Product(
            id = "13",
            name = "Round Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-006",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.ROUND_HOCKEY),
            shortDescription = "Features a full 360-degree round bristle head coupled with a hockey-style neck for superior bowl coverage.",
            features = listOf("360-degree cleaning head", "Curved neck design", "Durable grip handle"),
            specifications = mapOf("Style" to "Round Hockey", "Coverage" to "360 Degree", "Material" to "Virgin PP")
        ), Product(
            id = "14",
            name = "Big Round Hockey",
            category = "Toilet Brushes",
            modelNumber = "TB-007",
            images = listOf(Res.Image.Products.TOILET_BRUSHES.BIG_ROUND_HOCKEY),
            shortDescription = "An oversized 360-degree round toilet brush built for industrial sanitation needs and heavy usage.",
            features = listOf("Oversized 360-degree head", "Industrial-grade handle", "Fast cleaning action"),
            specifications = mapOf("Style" to "Round Hockey", "Size" to "Large", "Usage" to "Industrial / Commercial")
        ),

        // ==========================================
        // FLOOR WIPERS
        // ==========================================
        Product(
            id = "15",
            name = "12\" Floor Wiper",
            category = "Floor Wipers",
            modelNumber = "FW-012",
            images = listOf(Res.Image.Products.FLOOR_WIPERS.WIPER_12),
            shortDescription = "Compact 12-inch floor wiper ideal for bathrooms and small kitchens. Leaves floors instantly dry and streak-free.",
            features = listOf("High-quality rubber blade", "Lightweight plastic frame", "Leaves no water marks"),
            specifications = mapOf(
                "Blade Width" to "12 Inches",
                "Material" to "Rubber & Plastic",
                "Application" to "Household"
            )
        ), Product(
            id = "16",
            name = "18\" Floor Wiper",
            category = "Floor Wipers",
            modelNumber = "FW-018",
            images = listOf(Res.Image.Products.FLOOR_WIPERS.WIPER_18),
            shortDescription = "Commercial-grade 18-inch floor wiper for rapid liquid clearance on tile, marble, and large concrete surfaces.",
            features = listOf("Wide 18-inch rubber blade", "Reinforced handle joint", "Ideal for large areas"),
            specifications = mapOf(
                "Blade Width" to "18 Inches",
                "Material" to "Heavy-Duty Rubber & PP",
                "Application" to "Commercial"
            )
        ), Product(
            id = "17",
            name = "8\" Wiper",
            category = "Floor Wipers",
            modelNumber = "FW-008",
            images = listOf(Res.Image.Products.FLOOR_WIPERS.WIPER_8),
            shortDescription = "A specialized 8-inch handheld wiper perfect for glass, mirrors, and kitchen countertops.",
            features = listOf("Compact handheld design", "Precision rubber lip", "Streak-free cleaning"),
            specifications = mapOf(
                "Blade Width" to "8 Inches",
                "Type" to "Handheld",
                "Application" to "Glass & Counters"
            )
        ),

        // ==========================================
        // BROOMS & DUSTERS
        // ==========================================
        Product(
            id = "18",
            name = "Big Broom",
            category = "Brooms & Dusters",
            modelNumber = "BR-001",
            images = listOf(Res.Image.Products.BROOMS.BIG_BROOM),
            shortDescription = "A wide-sweeping big broom constructed with durable synthetic fibers for outdoor and large indoor spaces.",
            features = listOf("Wide sweeping path", "Fray-resistant synthetic bristles", "Sturdy handle attachment"),
            specifications = mapOf(
                "Size" to "Large",
                "Bristle Type" to "Synthetic",
                "Usage" to "Indoor/Outdoor sweeping"
            )
        ), Product(
            id = "19",
            name = "Medium Broom",
            category = "Brooms & Dusters",
            modelNumber = "BR-002",
            images = listOf(Res.Image.Products.BROOMS.MEDIUM_BROOM),
            shortDescription = "The standard medium broom, offering the perfect balance of flexibility and stiffness for daily household sweeping.",
            features = listOf("Perfect for tile and wood floors", "Lightweight design", "Dense bristle packing"),
            specifications = mapOf("Size" to "Medium", "Bristle Type" to "Synthetic", "Usage" to "Household sweeping")
        ), Product(
            id = "20",
            name = "Small Broom",
            category = "Brooms & Dusters",
            modelNumber = "BR-003",
            images = listOf(Res.Image.Products.BROOMS.SMALL_BROOM),
            shortDescription = "Compact sweeping broom ideal for tight spaces, apartments, and quick spot-cleaning tasks.",
            features = listOf("Highly maneuverable", "Easy to store", "Durable dust-catching bristles"),
            specifications = mapOf("Size" to "Small", "Bristle Type" to "Synthetic", "Usage" to "Spot cleaning")
        ), Product(
            id = "21",
            name = "Round Fan Brush",
            category = "Brooms & Dusters",
            modelNumber = "FB-001",
            images = listOf(Res.Image.Products.BROOMS.ROUND_FAN_BRUSH),
            shortDescription = "Designed specifically to reach and trap dust from ceiling fans, corners, and high fixtures safely.",
            features = listOf(
                "Round shape fits fan blades",
                "Dust-trapping synthetic fibers",
                "Compatible with extension poles"
            ),
            specifications = mapOf(
                "Shape" to "Round",
                "Application" to "Ceiling Fans & Cobwebs",
                "Material" to "Virgin PP"
            )
        ), Product(
            id = "22",
            name = "Folding V-Fan Brush",
            category = "Brooms & Dusters",
            modelNumber = "FB-002",
            images = listOf(Res.Image.Products.BROOMS.FOLDING_V_FAN_BRUSH),
            shortDescription = "Innovative V-shaped folding brush that wraps around fan blades to clean both top and bottom simultaneously.",
            features = listOf("V-Shape dual cleaning", "Folding mechanism for storage", "Traps heavy dust"),
            specifications = mapOf("Shape" to "V-Folding", "Application" to "Ceiling Fans", "Feature" to "Foldable")
        ),

        // ==========================================
        // DUST PANS
        // ==========================================
        Product(
            id = "23",
            name = "Dust Pan",
            category = "Dust Pans",
            modelNumber = "DP-001",
            images = listOf(Res.Image.Products.DUST_PANS.DUST_PAN),
            shortDescription = "A durable, high-capacity dust pan with a refined lip to ensure no dust is left behind during sweeping.",
            features = listOf("Deep capacity tray", "Precision floor lip", "Snap-on handle clip compatible"),
            specifications = mapOf(
                "Material" to "Impact-resistant Plastic",
                "Lip" to "Flush-to-floor",
                "Colors Available" to "Multiple"
            )
        ), Product(
            id = "24",
            name = "Dust Pan with Brush",
            category = "Dust Pans",
            modelNumber = "DP-002",
            images = listOf(Res.Image.Products.DUST_PANS.DUST_PAN_WITH_BRUSH),
            shortDescription = "A complete sweeping set. Includes our precision dust pan paired with a matching snap-in hand brush.",
            features = listOf("Convenient snap-in storage", "Dense sweeping brush included", "Ergonomic handles"),
            specifications = mapOf(
                "Set Includes" to "Pan & Hand Brush",
                "Storage" to "Nesting Design",
                "Material" to "Virgin PP"
            )
        ),

        // ==========================================
        // BASKETS & ACCESSORIES
        // ==========================================
        Product(
            id = "25",
            name = "Square Vegetable Basket",
            category = "Baskets & Accessories",
            modelNumber = "VB-001",
            images = listOf(Res.Image.Products.BASKETS_AND_ACCESSORIES.SQUARE_VEGETABLE_BASKET),
            shortDescription = "Ventilated square vegetable basket designed to keep produce fresh while maximizing pantry and fridge space.",
            features = listOf("Slotted ventilation design", "Food-grade BPA-free plastic", "Stackable shape"),
            specifications = mapOf("Shape" to "Square", "Material" to "Food-Safe PP", "Usage" to "Kitchen Storage")
        ), Product(
            id = "26",
            name = "Round Vegetable Basket",
            category = "Baskets & Accessories",
            modelNumber = "VB-002",
            images = listOf(Res.Image.Products.BASKETS_AND_ACCESSORIES.ROUND_VEGETABLE_BASKET),
            shortDescription = "Classic round ventilated basket perfect for washing, draining, and storing fruits and vegetables.",
            features = listOf("Excellent drainage slots", "Food-grade BPA-free plastic", "Durable rim for carrying"),
            specifications = mapOf("Shape" to "Round", "Material" to "Food-Safe PP", "Usage" to "Washing & Storage")
        ), Product(
            id = "27",
            name = "Cloth Clip",
            category = "Baskets & Accessories",
            modelNumber = "CC-001",
            images = listOf(Res.Image.Products.BASKETS_AND_ACCESSORIES.CLOTH_CLIP),
            shortDescription = "Heavy-duty plastic cloth clips with high-tension rust-proof springs. Designed to hold heavy wet garments securely.",
            features = listOf("High-tension grip", "Rust-resistant metal spring", "UV-resistant plastic"),
            specifications = mapOf(
                "Item Type" to "Laundry Pegs",
                "Spring" to "Rust-proof steel",
                "Packaging" to "Bulk Sets"
            )
        ),

        // ==========================================
        // SPECIALIZED
        // ==========================================
        Product(
            id = "28",
            name = "Solar Cleaning Big Brush",
            category = "Specialized",
            modelNumber = "SC-001",
            images = listOf(Res.Image.Products.SPECIALIZED.SOLAR_CLEANING_BRUSH),
            shortDescription = "Specialized cleaning system for solar panels. Features a 12-foot adjustable pipe and integrated water hose fitting to safely remove dust without scratching cells.",
            features = listOf(
                "Integrated water hose connection",
                "12-foot adjustable telescopic pipe",
                "Non-scratch ultra-soft bristles"
            ),
            specifications = mapOf(
                "Pipe Length" to "Up to 12 Feet",
                "Water Connection" to "Yes",
                "Application" to "Solar Panels & High Windows"
            )
        )
    )

    fun getProductByModel(modelNumber: String): Product? {
        return items.find { it.modelNumber.equals(modelNumber, ignoreCase = true) }
    }
}